import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'reactstrap';
import { Camera, RotateCcw } from 'react-feather';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { showToastError } from '@core/components/toastify/ToastTypes';

const VideoCapture = ({ photo, setPhoto }) => {
  const [isRequestingMedia, setIsRequestingMedia] = useState(false);
  const [haveDeviceAccess, setHaveDeviceAccess] = useState(false);
  const [videoDeviceId, setVideoDeviceId] = useState('');
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const audioInterval = useRef(null);
  const mediaChunks = useRef([]);

  const videoSource = 'camera';

  useEffect(() => {
    //
    // This updates the device list when the list changes. For example
    // plugging in or unplugging a mic or camera
    //
    navigator.mediaDevices.ondevicechange = getDevices;

    return () => stopUserMedia();
  }, []);

  useEffect(() => {
    if (videoDeviceId) {
      startAv();
    }
  }, [videoDeviceId]);

  const getDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const list = { video: [] };

    devices.forEach((device) => {
      if (device.kind === 'videoinput') {
        list.video.push(device);
      }
    });
  };

  const stopUserMedia = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        console.error('stopping track', track.kind, track.label);
        track.stop();
      });
    }
    streamRef.current = null;
  };

  /*
   * Stop all recording, cancel audio interval
   */
  const cleanup = () => {
    console.error('cleanup');
    mediaChunks.current = [];
    if (audioInterval.current) {
      clearInterval(audioInterval.current);
    }
  };

  /*
   * do a cleanup, and also cancel all media streams
   */
  const hardCleanup = () => {
    cleanup();
    stopUserMedia();
    setHaveDeviceAccess(false);
    setVideoDeviceId('');
  };

  const startAv = () => {
    cleanup();
    if (!videoSource) {
      console.error('Cannot startAv without a video source');
      return;
    }
    if (videoSource === 'camera') {
      startCamera();
    }
  };

  const setupStream = (stream) => {
    if (videoSource === 'camera') {
      streamRef.current = stream;
      if (videoRef.current !== null) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setHaveDeviceAccess(true);
    }
  };

  const startCamera = async () => {
    if (navigator.mediaDevices) {
      const video = videoDeviceId ? { deviceId: videoDeviceId } : true;
      const constraints = { video };
      try {
        await getDevices();
        console.error('requesting user media with constraints', constraints);

        stopUserMedia();
        setIsRequestingMedia(true);
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        setIsRequestingMedia(false);
        await getDevices();
        setupStream(stream);
      } catch (err) {
        console.error('getdevices error', err);
        setIsRequestingMedia(false);
        showToastError(
          'Error getting device access. If denied, please enable it in your browser settings.',
        );
      }
    } else {
      showToastError('navigator.mediaDevices is not supported in this browser');
    }
    return function teardown() {
      hardCleanup();
    };
  };

  const handleCapture = () => {
    const video = document.getElementById('video');
    const canvas = document.getElementById('canvas');
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, 640, 480);
    setPhoto(true);
  };

  const handleReset = () => {
    setPhoto(false);
  };

  return (
    <div>
      <div>
        <p>You need to take a photo before proceeding with the test</p>
        <div className="video-container">
          <video
            className={classnames(photo && 'd-none')}
            id="video"
            ref={videoRef}
            width="640"
            height="480"
            autoPlay
          />
          <canvas
            style={{ borderRadius: 30 }}
            id="canvas"
            width="640"
            height="480"
            className={classnames(photo ? 'd-block' : 'd-none')}
          />
        </div>
        <div className="skeleton-container">
          {!haveDeviceAccess && videoSource === 'camera' ? (
            <Button.Ripple onClick={startCamera}>
              {isRequestingMedia
                ? 'Loading device...'
                : 'Allow the browser to use your camera to start'}
            </Button.Ripple>
          ) : (
            <>
              {photo ? (
                <Button
                  color="primary mt-1 d-flex align-items-center"
                  className="round"
                  onClick={handleReset}
                >
                  <RotateCcw size={16} />{' '}
                  <span style={{ marginLeft: 4 }}>Reset</span>
                </Button>
              ) : (
                <Button
                  color="primary mt-1 d-flex align-items-center"
                  className="round"
                  onClick={handleCapture}
                >
                  <Camera size={16} />{' '}
                  <span style={{ marginLeft: 8 }}>Capture</span>
                </Button>
              )}
            </>
          )}
        </div>
        {/*<div className="centerDiv">*/}
        {/*  <GroupButtonSubmit*/}
        {/*      onCancel={handleCancel}*/}
        {/*      disabled={isLoadingPreview || isLoading}*/}
        {/*  >*/}
        {/*    <Button.Ripple*/}
        {/*        color="primary"*/}
        {/*        className="round"*/}
        {/*        onClick={submitRecording}*/}
        {/*        disabled={isLoadingPreview || isLoading}*/}
        {/*    >*/}
        {/*      {isLoadingPreview ? 'Loading preview...' : titleSubmit}*/}
        {/*    </Button.Ripple>*/}
        {/*  </GroupButtonSubmit>*/}
        {/*</div>*/}
      </div>
      <style jsx>
        {`
          .error-message {
            color: #c9473f;
            max-width: 400px;
            padding-bottom: 20px;
            text-align: center;
            line-height: 24px;
          }

          .skeleton-container {
            width: 100%;
            display: flex;
            justify-content: center;
          }

          .video-container {
            position: relative;
            display: flex;
            justify-content: center;
          }

          video {
            display: ${haveDeviceAccess ? 'block' : 'none'};
            border-radius: 30px;
          }

          video.mirror-image {
            -webkit-transform: scaleX(-1);
            transform: scaleX(-1);
          }

          h1 {
            margin-top: 3vh;
            font-size: 3.5vw;
          }

          .centerDiv {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 20px;
          }
        `}
      </style>
    </div>
  );
};

VideoCapture.propTypes = {
  photo: PropTypes.bool.isRequired,
  setPhoto: PropTypes.func.isRequired,
};

export default VideoCapture;
